import InboxConversationTransferAPI from '../../api/inboxConversationTransfer';

const state = {
  records: [],
  meta: { currentPage: 1, totalCount: 0, perPage: 25 },
  currentTransfer: null,
  previewResult: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isPreviewing: false,
    isCreating: false,
    isCancelling: false,
    isRetrying: false,
  },
  error: null,
};

const getters = {
  getTransfers: $state => $state.records,
  getMeta: $state => $state.meta,
  getCurrentTransfer: $state => $state.currentTransfer,
  getPreviewResult: $state => $state.previewResult,
  getUIFlags: $state => $state.uiFlags,
  getError: $state => $state.error,
};

const upsertRecord = ($state, transfer) => {
  const index = $state.records.findIndex(item => item.id === transfer.id);
  if (index === -1) {
    $state.records.unshift(transfer);
  } else {
    $state.records.splice(index, 1, transfer);
  }
};

const actions = {
  async fetchTransfers({ commit }, { inboxId, ...params } = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    commit('SET_ERROR', null);
    try {
      const { data } = await InboxConversationTransferAPI.fetchTransfers(
        inboxId,
        params
      );
      commit('SET_TRANSFERS', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchTransfer({ commit }, { inboxId, transferId }) {
    commit('SET_UI_FLAG', { isFetchingItem: true });
    commit('SET_ERROR', null);
    try {
      const { data } = await InboxConversationTransferAPI.fetchTransfer(
        inboxId,
        transferId
      );
      commit('SET_CURRENT_TRANSFER', data);
      commit('UPSERT_TRANSFER', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingItem: false });
    }
  },

  async previewTransfer({ commit }, { inboxId, payload }) {
    commit('SET_UI_FLAG', { isPreviewing: true });
    commit('SET_ERROR', null);
    try {
      const { data } = await InboxConversationTransferAPI.preview(
        inboxId,
        payload
      );
      commit('SET_PREVIEW_RESULT', data);
      return data;
    } catch (error) {
      const responseData = error.response && error.response.data;
      if (responseData) {
        commit('SET_PREVIEW_RESULT', responseData);
      }
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isPreviewing: false });
    }
  },

  async createTransfer({ commit }, { inboxId, payload }) {
    commit('SET_UI_FLAG', { isCreating: true });
    commit('SET_ERROR', null);
    try {
      const { data } = await InboxConversationTransferAPI.createTransfer(
        inboxId,
        payload
      );
      commit('SET_CURRENT_TRANSFER', data);
      commit('UPSERT_TRANSFER', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async cancelTransfer({ commit }, { inboxId, transferId }) {
    commit('SET_UI_FLAG', { isCancelling: true });
    try {
      const { data } = await InboxConversationTransferAPI.cancelTransfer(
        inboxId,
        transferId
      );
      commit('SET_CURRENT_TRANSFER', data);
      commit('UPSERT_TRANSFER', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCancelling: false });
    }
  },

  async retryTransfer({ commit }, { inboxId, transferId }) {
    commit('SET_UI_FLAG', { isRetrying: true });
    try {
      const { data } = await InboxConversationTransferAPI.retryTransfer(
        inboxId,
        transferId
      );
      commit('SET_CURRENT_TRANSFER', data);
      commit('UPSERT_TRANSFER', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isRetrying: false });
    }
  },

  resetState({ commit }) {
    commit('RESET_STATE');
  },

  clearPreview({ commit }) {
    commit('SET_PREVIEW_RESULT', null);
  },

  clearCurrentTransfer({ commit }) {
    commit('SET_CURRENT_TRANSFER', null);
  },
};

const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_TRANSFERS($state, payload) {
    $state.records = payload.data || [];
    const meta = payload.meta || {};
    $state.meta = {
      currentPage: meta.current_page || 1,
      totalCount: meta.total_count || 0,
      perPage: meta.per_page || 25,
    };
  },

  SET_CURRENT_TRANSFER($state, transfer) {
    $state.currentTransfer = transfer;
  },

  UPSERT_TRANSFER($state, transfer) {
    if (!transfer) return;
    upsertRecord($state, transfer);
  },

  SET_PREVIEW_RESULT($state, result) {
    $state.previewResult = result;
  },

  SET_ERROR($state, error) {
    $state.error = error;
  },

  RESET_STATE($state) {
    $state.records = [];
    $state.meta = { currentPage: 1, totalCount: 0, perPage: 25 };
    $state.currentTransfer = null;
    $state.previewResult = null;
    $state.error = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
